// Hook para manejar los filtros del dashboard
// Dashboard filters hook

import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useDashboardRefreshing } from './useDashboardRefreshing';
import { useMemo } from 'react';

export interface DashboardFilters {
  shop_id?: number;
  cutoff_time?: string;
  date?: string;
}

export const useDashboardFilters = () => {
  const { params, updateParam, updateParams, resetParams, hasParams } = useQueryParams({
    shop_id: { type: 'string', default: 'all' },
    cutoff_time: { type: 'string', default: '14:00' },
    date: { type: 'string', default: '' }, // Formato Y-m-d
  });

  const { isRefreshing, startRefreshing, completeRefreshing } = useDashboardRefreshing();

  // Convertir parámetros para las APIs (memoizado para evitar recreación constante)
  const apiFilters = useMemo(() => {
    const filters: DashboardFilters = {};

    // Convertir shop_id a número si no es 'all'
    if (params.shop_id !== 'all') {
      filters.shop_id = parseInt(params.shop_id, 10);
    }

    // Usar cutoff_time (siempre incluir)
    filters.cutoff_time = params.cutoff_time;

    // Usar fecha si está definida
    if (params.date) {
      filters.date = params.date;
    }

    return filters;
  }, [params.shop_id, params.cutoff_time, params.date]);

  // Función legacy para compatibilidad
  const getApiFilters = () => apiFilters;

  // Convertir shop_id para el hook useShops
  const getShopId = (): string => {
    return params.shop_id;
  };

  // Manejar cambios de filtros con estado de refreshing
  const handleFilterChange = (key: string, value: string) => {
    startRefreshing();
    updateParam(key, value);
  };

  // Función para marcar como no refreshing (llamada desde los hooks de KPIs)
  const setRefreshingComplete = completeRefreshing;

  return {
    params,
    updateParam: handleFilterChange,
    updateParams,
    resetParams,
    hasParams,
    getApiFilters,
    apiFilters, // Exportar directamente los filtros memoizados
    getShopId,
    isRefreshing,
    setRefreshingComplete,
  };
};
